import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { flatScreens } from '../../lib/nav';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from '../ui/command';

export const OPEN_QUICK_SEARCH_EVENT = 'tricorps360:open-quick-search';
export function openQuickSearch() { window.dispatchEvent(new CustomEvent(OPEN_QUICK_SEARCH_EVENT)); }

/**
 * Ctrl/Cmd+K quick search. Jumps to any SCREEN the user can open, by name.
 * It is built from the resolved navigation, so it never lists a screen the
 * user has no permission to reach.
 */
export function QuickSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = useEffectivePermissions();

  const screens = useMemo(
    () => flatScreens({ role: user?.role, perms }),
    [user?.role, perms]
  );

  const byGroup = useMemo(() => {
    const m = new Map();
    for (const s of screens) {
      if (!m.has(s.group)) m.set(s.group, []);
      m.get(s.group).push(s);
    }
    return [...m.entries()];
  }, [screens]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_QUICK_SEARCH_EVENT, onOpen);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_QUICK_SEARCH_EVENT, onOpen);
    };
  }, []);

  const go = useCallback((href) => { setOpen(false); navigate(href); }, [navigate]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search screens…" data-testid="quick-search-input" />
      <CommandList>
        <CommandEmpty>No screens found.</CommandEmpty>
        {byGroup.map(([group, items], gi) => (
          <React.Fragment key={group}>
            {gi > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {items.map((s) => (
                <CommandItem
                  key={s.href}
                  value={`${s.name} ${s.group}`}
                  onSelect={() => go(s.href)}
                  data-testid={`quick-search-item`}
                >
                  {s.icon && <s.icon className="mr-2 h-4 w-4 opacity-70" />}
                  <span>{s.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

export default QuickSearch;
